import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Shell, ROLE_LABEL } from "@/app/Shell";
import { useMe } from "@/app/session";
import { RequestError } from "@/app/queries";
import { exportUrl, importNotebook, nbKeys, runnerAvailable, useAssignment, useWorkspace, type AssignmentView, type WorkspaceData } from "@/app/notebookQueries";
import { Button } from "@/ui/Button";
import { NotFound, SkeletonRows, StateCard, Toast } from "@/ui/states";
import { count } from "@/lib/plural";
import { src, type Task } from "@/kernel/runner";
import { EnvPanel } from "./notebook/EnvPanel";
import { OfflineWorkspace } from "./notebook/OfflineWorkspace";
import { ServerSubmit } from "./notebook/ServerSubmit";
import { Sidebar } from "./notebook/Sidebar";
import { TaskView } from "./notebook/TaskView";
import { ReadOnlyNotebook } from "./notebook/ReadOnlyNotebook";
import { useAutosave } from "./notebook/useAutosave";
import { useNotebookSession } from "./notebook/useNotebookSession";
import { useIsMobile } from "./step/model";

const ddmm = (iso: string) => iso.slice(8, 10) + "." + iso.slice(5, 7);

function daysLeft(iso: string) {
  const d = Math.round((Date.parse(iso) - new Date(new Date().toDateString()).getTime()) / 86_400_000);
  return d === 0 ? "сегодня" : d > 0 ? `осталось ${count(d, "день", "дня", "дней")}` : `просрочено на ${count(-d, "день", "дня", "дней")}`;
}

/** S5 «Рабочее место»: ноутбук в браузере на Pyodide. На телефоне — только чтение (S8). */
/** task — задача, открытая при входе (из S6: «открыть ячейку», клик по сетке). */
export function NotebookPage({ assignmentId, task }: { assignmentId: string; task?: number }) {
  const mobile = useIsMobile();
  const me = useMe();
  const asg = useAssignment(assignmentId);
  const ws = useWorkspace(assignmentId);
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;

  if (mobile) return <ReadOnlyNotebook assignmentId={assignmentId} />;
  const err = asg.error ?? ws.error;
  if (err instanceof RequestError && err.code === "step_locked")
    return (
      <Shell nav user={user}>
        <StateCard code="LOCKED" where="домашка" title="Шаг ещё закрыт" body={err.message} primary={{ label: "вернуться к маршруту", href: "/" }} />
      </Shell>
    );
  if (err instanceof RequestError && err.status === 404)
    return (
      <Shell nav user={user}>
        <NotFound />
      </Shell>
    );
  if (err)
    return (
      <Shell nav user={user}>
        <StateCard code="ERR_NETWORK" where="ноутбук" tone="warn" title="Нет связи с сервером" body="Ноутбук не загрузился. Черновик хранится на сервере и в браузере — ничего не потеряно." primary={{ label: "повторить сейчас", onClick: () => (void asg.refetch(), void ws.refetch()) }} />
      </Shell>
    );
  if (!asg.data || !ws.data)
    return (
      <Shell nav user={user}>
        <SkeletonRows />
      </Shell>
    );
  if (!runnerAvailable(asg.data.manifest.runtime)) return <OfflineWorkspace asg={asg.data} ws={ws.data} user={user} />;
  return <Workspace asg={asg.data} ws={ws.data} user={user} initialTask={task} />;
}

function Workspace({ asg, ws, user, initialTask }: { asg: AssignmentView; ws: WorkspaceData; user?: { name: string; role: string }; initialTask?: number }) {
  const qc = useQueryClient();
  const autosave = useAutosave(asg.id, ws.updated_at);
  const onChange = useCallback((nb: WorkspaceData["notebook"], res: WorkspaceData["results"]) => autosave.schedule(nb, res), [autosave]);
  const s = useNotebookSession(asg, ws, onChange);
  // Локальная копия новее серверной (перезагрузка до сохранения, офлайн) — берём её.
  const { replace } = s;
  useEffect(() => {
    if (autosave.restored) replace(autosave.restored.notebook, autosave.restored.results);
  }, [autosave.restored, replace]);
  const tasks = s.tasks;
  const [selected, setSelected] = useState<number>(() => (initialTask && tasks.some((t) => t.n === initialTask) ? initialTask : (tasks.find((t) => s.results[String(t.n)]?.status !== "passed")?.n ?? tasks[0]?.n ?? 1)));
  const [toast, setToast] = useState<{ tone: "ok" | "err" | "info"; title: string; body: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const task = tasks.find((t) => t.n === selected) ?? tasks[0];

  const story = useMemo(() => {
    const first = s.notebook.cells.find((c) => c.cell_type === "markdown");
    const m = first ? /·\s*(.+)$/m.exec(src(first).split("\n")[0] ?? "") : null;
    return m?.[1]?.trim();
  }, [s.notebook]);

  const checksCount = useCallback((t: Task) => (s.notebook.cells.find((c) => c.id === t.checks_cell_id) ? (src(s.notebook.cells.find((c) => c.id === t.checks_cell_id)!).match(/^\s*assert\b/gm) ?? []).length : 0), [s.notebook]);
  const next = task ? tasks.find((t) => t.n > task.n && s.results[String(t.n)]?.status !== "passed") ?? tasks.find((t) => t.n !== task.n && s.results[String(t.n)]?.status !== "passed") : undefined;

  const onImport = async (file: File) => {
    try {
      const v = await importNotebook(asg.id, file);
      await qc.invalidateQueries({ queryKey: nbKeys.workspace(asg.id) });
      const fresh = qc.getQueryData<WorkspaceData>(nbKeys.workspace(asg.id)) ?? (await qc.fetchQuery({ queryKey: nbKeys.workspace(asg.id) }));
      if (fresh) s.replace(fresh.notebook, fresh.results);
      setToast({ tone: "ok", title: `Версия ${v.n} из Colab`, body: "Черновик заменён файлом из Colab. Запустите задачи заново — результаты сброшены." });
    } catch (e) {
      setToast({ tone: "err", title: "Файл не принят", body: e instanceof Error ? e.message : "Не удалось загрузить ноутбук." });
    }
  };

  if (!task) return null;
  const saveBanner = autosave.state.kind === "offline" || autosave.state.kind === "error";

  return (
    <Shell wide user={user}>
      <div className="flex h-[calc(100vh-55px)] flex-col">
        <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5">
          <span className="font-mono text-[11px] text-mut">
            домашки / {asg.track.toLowerCase()} · {asg.step_title.toLowerCase()}
          </span>
          <span className="font-mono text-[13px] font-semibold text-ink" data-testid="file-name">
            {asg.file_name}
          </span>
          <span className="rounded-sm border border-bd2 px-2 py-[2px] font-mono text-[11px] text-ink2">черновик</span>
          <span className="flex-1" />
          {asg.deadline && (
            <span className="font-mono text-[11px] text-ink2">
              дедлайн {ddmm(asg.deadline)} · {daysLeft(asg.deadline)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2.5 border-b border-bd bg-bg2 px-5 py-2.5">
          <EnvPanel status={s.status} lastRun={s.lastRun} save={autosave.state} />
          <span className="flex-1" />
          <Button size="sm" onClick={() => void s.runTask(task)} disabled={s.running !== null || s.status.state === "dead"}>
            ▷ запустить ячейку
          </Button>
          <Button size="sm" onClick={() => void s.runAll()} disabled={s.running !== null || s.status.state === "dead"}>
            запустить всё
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void s.restart()} disabled={s.running !== null}>
            перезапустить ядро
          </Button>
          <a href={exportUrl(asg.id)} download className="no-underline" onClick={() => void autosave.flush()}>
            <Button size="sm">открыть в Colab</Button>
          </a>
          <Button size="sm" variant="ghost" onClick={() => fileInput.current?.click()}>
            вернуть из Colab
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".ipynb,application/x-ipynb+json"
            className="hidden"
            data-testid="import-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImport(f);
              e.target.value = "";
            }}
          />
        </div>
        {saveBanner && (
          <div className="border-b border-warn bg-warnsoft px-5 py-2 text-[12.5px] text-ink2" role="status" data-testid="offline-banner">
            <strong className="text-ink">Нет связи с сервером.</strong> Правки лежат в браузере и отправятся сами, когда связь вернётся. Не закрывай вкладку.{" "}
            <button type="button" onClick={() => void autosave.flush()} className="border-0 bg-transparent font-mono text-[12px] underline" style={{ color: "var(--ink)" }}>
              повторить сейчас
            </button>
          </div>
        )}
        <div className="grid min-h-0 flex-1 grid-cols-[302px_minmax(0,1fr)]">
          <Sidebar tasks={tasks} results={s.results} checksCount={checksCount} selected={task.n} onSelect={setSelected} footer={<ServerSubmit assignmentId={asg.id} flush={autosave.flush} />} />
          <TaskView
            nb={s.notebook}
            task={task}
            total={tasks.length}
            story={story}
            result={s.results[String(task.n)]}
            run={s.runs[String(task.n)]}
            running={s.running === task.n || s.running === "all"}
            status={s.status}
            code={s.code(task)}
            onCode={(v) => s.setCode(task, v)}
            onRun={() => void s.runTask(task)}
            onRestart={() => void s.restart()}
            next={next}
            onNext={() => next && setSelected(next.n)}
          />
        </div>
      </div>
      {toast && (
        <div className="fixed bottom-4 right-4 z-30 w-[440px] max-w-[calc(100vw-32px)]">
          <Toast tone={toast.tone} title={toast.title} body={toast.body} action={{ label: "закрыть", onClick: () => setToast(null) }} />
        </div>
      )}
    </Shell>
  );
}
